/** Tab ids and portfolio stats for Planting Assistant (area calculator) V2 shell. */

export const PLANTING_ASSISTANT_TABS = [
  { id: 'plots', label: 'My Plots' },
  { id: 'calculator', label: 'Seed Calculator' },
  { id: 'stores', label: 'Seed Stores' },
  { id: 'history', label: 'History' },
];

/**
 * Zone-agnostic portfolio stats for the My Plots tab stat row.
 * USDA zone is intentionally omitted — zones are per saved area group.
 */
export function buildPortfolioStats({
  polygonCount = 0,
  totalAcres = 0,
  savedGroupCount = 0,
  selectedPolygonCount = 0,
  selectedAcres = 0,
} = {}) {
  return {
    polygonCount: Math.max(0, polygonCount),
    totalAcres: Math.max(0, totalAcres),
    savedGroupCount: Math.max(0, savedGroupCount),
    selectedPolygonCount: Math.max(0, selectedPolygonCount),
    selectedAcres: Math.max(0, selectedAcres),
  };
}

export function formatPortfolioAcres(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '—';
  }
  return value.toFixed(1);
}

/** Acreage number for plot row badge (mock shows raw acres, not "X ac"). */
export function formatPlotAcreageBadge(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '—';
  }
  return value.toFixed(2);
}

export function getPlotScopeLabel(poly, mode, selectedClubName) {
  if (mode === 'club') {
    return selectedClubName || 'Club';
  }
  return poly?.club || 'Personal';
}

/** Default name when combining selected polygons into a new area group. */
export function buildDefaultGroupName(selectedPolygons = [], totalAcres = 0) {
  if (!Array.isArray(selectedPolygons) || selectedPolygons.length === 0) return '';
  if (selectedPolygons.length === 1) {
    return selectedPolygons[0].name || 'Unnamed polygon';
  }
  const acresLabel = typeof totalAcres === 'number' && !Number.isNaN(totalAcres)
    ? totalAcres.toFixed(2)
    : '0.00';
  return `${selectedPolygons.length} polygons (${acresLabel} ac)`;
}

/** Client-side lbs/bags estimate from preset defaults (no soil/zone adjustments). */
export function computePresetEstimate(preset, acres) {
  if (!preset || typeof acres !== 'number' || Number.isNaN(acres) || acres <= 0) {
    return {
      rateLbsPerAcre: 0,
      totalLbs: 0,
      bagsNeeded: 0,
      bagWeightLbs: 50,
    };
  }
  const rateLbsPerAcre = Number(preset.default_rate_lbs_per_acre) || 0;
  const bagWeightLbs = Number(preset.bag_weight_lbs) || 50;
  const totalLbs = rateLbsPerAcre * acres;
  const bagsNeeded = bagWeightLbs > 0 ? Math.ceil(totalLbs / bagWeightLbs) : 0;
  return { rateLbsPerAcre, totalLbs, bagsNeeded, bagWeightLbs };
}

/** Merge API recommendation rows with preset preview for calculator breakdown UI. */
export function buildCalculatorBreakdown({
  acres = 0,
  seedPreset,
  fertilizerPreset,
  seedId,
  fertilizerId,
  recommendationResult,
} = {}) {
  const seedFromApi = recommendationResult?.seed;
  const fertFromApi = recommendationResult?.fertilizer;

  const seedPreview = seedId && seedPreset ? computePresetEstimate(seedPreset, acres) : null;
  const fertPreview = fertilizerId && fertilizerPreset
    ? computePresetEstimate(fertilizerPreset, acres)
    : null;

  const seed = seedFromApi
    ? {
        label: seedFromApi.label,
        rateLbsPerAcre: seedFromApi.rate_lbs_per_acre,
        totalLbs: seedFromApi.total_lbs,
        bagsNeeded: seedFromApi.bags_needed,
        bagWeightLbs: seedFromApi.bag_weight_lbs,
        isPreview: false,
      }
    : seedPreview
      ? {
          label: seedPreset.label,
          rateLbsPerAcre: seedPreview.rateLbsPerAcre,
          totalLbs: seedPreview.totalLbs,
          bagsNeeded: seedPreview.bagsNeeded,
          bagWeightLbs: seedPreview.bagWeightLbs,
          isPreview: true,
        }
      : null;

  const fertilizer = fertFromApi
    ? {
        label: fertFromApi.label,
        rateLbsPerAcre: fertFromApi.rate_lbs_per_acre,
        totalLbs: fertFromApi.total_lbs,
        bagsNeeded: fertFromApi.bags_needed,
        bagWeightLbs: fertFromApi.bag_weight_lbs,
        isPreview: false,
      }
    : fertPreview
      ? {
          label: fertilizerPreset.label,
          rateLbsPerAcre: fertPreview.rateLbsPerAcre,
          totalLbs: fertPreview.totalLbs,
          bagsNeeded: fertPreview.bagsNeeded,
          bagWeightLbs: fertPreview.bagWeightLbs,
          isPreview: true,
        }
      : null;

  const totalLbs = (seed?.totalLbs || 0) + (fertilizer?.totalLbs || 0);

  return { seed, fertilizer, totalLbs };
}
